import sortBy from 'lodash/sortBy';

import { createThunkRequest, DELETE, GET, PATCH, POST, PUT } from '@webapp/account/src/libs/request';
import { getFilterParams, getReportSsid } from '@webapp/account/src/resources/lib';
import type { AnswerReport } from '@webapp/account/src/resources/reports/answers';
import { buildReportParams, fetchReportsStat } from '@webapp/account/src/resources/reports/fetch';
import type { GlobalFilter } from '@webapp/account/src/resources/reports/global-filters';
import { getFormattedReports } from '@webapp/account/src/resources/reports/lib';
import { mapAnswer, mapReport } from '@webapp/account/src/resources/reports/map';
import { mapLinkStat } from '@webapp/account/src/resources/reports/map-report';
import type { BaseReport, Report, Row } from '@webapp/account/src/resources/reports/reports';
import {
    selectCrossTabs,
    selectLinkStatsById,
    selectReports,
    selectReportsQuestions,
} from '@webapp/account/src/resources/reports/selectors';
import { selectActiveSurvey, selectCurrentSurveyId } from '@webapp/account/src/resources/survey/selectors';

import { REPORT_RESULTS_PAGE_SIZE } from '@webapp/common/lib/const';
import type { ChartGrouping } from '@webapp/common/lib/date';
import { mapCategory } from '@webapp/common/lib/map';
import { LocalStorage } from '@webapp/common/lib/storage';
import { parseJSON, striptags } from '@webapp/common/lib/utils';
import type { Question } from '@webapp/common/resources/entities/question';
import { reorderQuestions } from '@webapp/common/resources/question-types';
import { AnswerType, QuestionType } from '@webapp/common/resources/survey';

const commonReportParams = (
    globalFilters: GlobalFilter,
    reports: Array<Report | Question>,
    question: Array<number>,
    page: number,
    limit: number,
): AnyObject => buildReportParams(globalFilters, reports, question, page, limit);

// Функция для замены {Q-ID} на {Qorder} в тексте
export const replaceQuestionRefs = <T>(text: T, questionsMapped: Array<{ id: number; order?: number }>): T => {
    if (typeof text !== 'string') {
        return text;
    }
    return text.replace(/\{Q-([0-9]+)\}/gi, (_match, id) => {
        const q = questionsMapped.find((q) => q.id === parseInt(id, 10));
        if (!q || !q.order) return '';
        return `{Q${q.order}}`;
    }) as T;
};

const getQuestionsRequest = async ({ surveyId }: { surveyId: number }) => {
    const { questions } = await POST('list/question', {
        survey: surveyId,
    });

    const reportsRaw: Array<Report> = reorderQuestions(questions).map(mapReport);
    const questionsMapped = reportsRaw.map(({ question }) => question);

    return {
        reports: reportsRaw.map((report) => ({
            ...report,
            question: ((quest) => {
                if (quest.type === QuestionType.TEXT_BLOCK) {
                    return {
                        ...quest,
                        params: {
                            ...quest.params,
                            textBlock: replaceQuestionRefs(quest.params.textBlock, questionsMapped),
                        },
                    };
                }
                return {
                    ...quest,
                    name: replaceQuestionRefs(quest.name, questionsMapped),
                    cols: quest.cols.map((col) => {
                        return {
                            ...col,
                            name: replaceQuestionRefs(col.name, questionsMapped),
                        };
                    }),
                    rows: quest.rows.map((row) => {
                        return {
                            ...row,
                            value: replaceQuestionRefs(row.value, questionsMapped),
                            value2: replaceQuestionRefs(row.value2, questionsMapped),
                            value3: replaceQuestionRefs(row.value3, questionsMapped),
                        };
                    }),
                };
            })(report.question),
        })),
    };
};

export const getQuestions = createThunkRequest(
    'reports/getQuestions',
    async ({ surveyId }: { surveyId: number }) => {
        const { reports } = await getQuestionsRequest({ surveyId });

        return reports;
    },
    { notify: false },
);

export const getQuestionsForReports = createThunkRequest(
    'reports/getQuestionsForReports',
    async (
        {
            surveyId,
            globalFilters,
            responsesLimit,
            initialCount,
            page = 1,
        }: {
            surveyId: number;
            globalFilters: GlobalFilter;
            responsesLimit: number;
            initialCount: number;
            page: number;
        },
        { dispatch },
    ) => {
        const { reports } = await getQuestionsRequest({
            surveyId,
        });

        const initiallyLoadedQuestion = reports.slice(0, initialCount - 1);

        await dispatch(
            getReports({
                reports: initiallyLoadedQuestion,
                globalFilters,
                responsesLimit,
                page,
                upsertQuestions: true,
                allReports: reports,
            }),
        );
        return reports;
    },
    { notify: false },
);

export const refreshQuestions = createThunkRequest(
    'reports/refreshQuestions',
    async ({ surveyId }: { surveyId: number }) => {
        const { questions } = await POST('list/question', {
            survey: surveyId,
            lang: LocalStorage.get(`survey_${surveyId}_results_lang`) || undefined,
        });
        const reports = getFormattedReports(questions);

        return reports.reduce((acc, item) => {
            acc[item.id] = item;

            return acc;
        }, {});
    },
    { notify: false },
);

export const getLinksStat = createThunkRequest(
    'reports/getLinksStat',
    async (
        { surveyId, paramIds = [], offset = 0 }: { surveyId: number; paramIds?: Array<number>; offset?: number },
        { getState },
    ) => {
        const { globalFilters, reports } = selectReports(getState());
        const commonFilterParams = getFilterParams(globalFilters, reports);
        const ssid = getReportSsid(surveyId, commonFilterParams);
        const page = paramIds.length > 0 ? Math.floor(offset / 10) + 1 : 1;
        const limit = 10;
        const pagination =
            paramIds.length > 0
                ? {
                      page,
                      paramIds,
                      limit,
                  }
                : { page, limit };

        const [{ list }, { stat }] = await Promise.all([
            POST('list/link_params', { survey: surveyId, ...pagination }),
            POST('stat/link_params', {
                survey: surveyId,
                ssid,
                ...commonFilterParams,
                ...pagination,
            }),
        ]);

        return {
            data: mapLinkStat(stat, globalFilters, list),
            page,
            paramIds,
        };
    },
    { notify: false },
);

export const loadMore = createThunkRequest(
    'reports/getMoreResults',
    async (
        {
            report,
            inItems,
            field,
        }: {
            report: Report;
            inItems: boolean;
            rows?: Array<Row>;
            field?: string;
        },
        { dispatch, getState },
    ) => {
        const { globalFilters, responsesLimit, reports } = selectReports(getState());
        const stateReport = reports.find(({ id }) => id === report.id);
        // Страницу берём из состояния, а не из rows.length: после маппинга часть
        // строк может отбрасываться (mapSelfSimple → null), и
        // floor(rows.length / limit) + 1 ошибочно давал page=1 (FRONT-74).
        const lastPage = field === 'comments' ? stateReport?.commentsPage : stateReport?.responsesPage;

        if (!stateReport || !lastPage) {
            // Реальный баг пагинации: базовая страница не загружена/потеряна.
            // Оставляем error, чтобы сигнал не потерялся в Sentry.
            console.error('pagination broken', report);
            return;
        }

        const page = lastPage + 1;

        await dispatch(
            getReports({
                reports: [report],
                globalFilters,
                responsesLimit,
                page,
                inItems,
                field,
            }),
        );
        return {};
    },
    { notify: false },
);

export const removeFilter = createThunkRequest(
    'reports/removeFilter',
    async ({ filter }: { filter: BaseReport }, { dispatch, getState }) => {
        const { globalFilters, reports, responsesLimit } = selectReports(getState());
        const report = reports.find(({ id }) => id === filter.id);

        if (report) {
            await dispatch(
                getReports({
                    reports: [report],
                    globalFilters,
                    responsesLimit,
                    page: 1,
                    upsertQuestions: true,
                }),
            );
        }
        return {};
    },
    { notify: false },
);

export const upsertQuestions = createThunkRequest(
    'reports/upsertQuestions',
    async ({ reportId }: { reportId: number | Array<number> }, { dispatch, getState }) => {
        const { globalFilters, questions, responsesLimit } = selectReports(getState());

        if (!Array.isArray(reportId)) {
            reportId = [reportId];
        }

        const reports = reportId.map((reportId) => questions.find(({ id }) => id === reportId));

        await dispatch(
            getReports({
                reports,
                globalFilters,
                responsesLimit,
                page: 1,
                upsertQuestions: true,
            }),
        );
        return {};
    },
    { notify: false },
);
export const setGrouping = createThunkRequest(
    'reports/getWithGrouping',
    async ({ report, group }: { report: Report; group: ChartGrouping }, { dispatch, getState }) => {
        const { globalFilters, responsesLimit } = selectReports(getState());
        await dispatch(
            getReports({
                reports: [report],
                globalFilters,
                responsesLimit,
                page: 1,
                field: 'group',
                group,
            }),
        );
        return {};
    },
    { notify: false },
);

export const getReports = createThunkRequest(
    'reports/getReports',
    async (
        {
            reports,
            globalFilters,
            responsesLimit,
            page,
            inItems,
            field,
            group,
            upsertQuestions,
            allReports,
        }: {
            reports: Array<Report>;
            globalFilters: GlobalFilter;
            responsesLimit: number;
            page?: number;
            inItems?: boolean;
            field?: string;
            group?: ChartGrouping;
            upsertQuestions?: boolean;
            allReports?: Array<Report>;
        },
        { getState },
    ) => {
        const state = getState();
        const selectedQuestions = selectReportsQuestions(state);
        const allReportList = allReports || selectedQuestions;
        const { id: surveyId } = selectActiveSurvey(state);

        const {
            reports: mappedReports,
            filesSize,
            filesCount,
        } = await fetchReportsStat({
            surveyId,
            reports,
            allReportList,
            globalFilters,
            responsesLimit,
            page,
            field,
            group,
        });

        return {
            reports: mappedReports,
            page,
            inItems,
            field,
            upsertQuestions,
            filesSize,
            filesCount,
        };
    },
    { notify: false },
);

export const getWordStat = createThunkRequest(
    'reports/getWordStat',
    async (
        {
            questionIds,
            questions,
            globalFilters,
        }: {
            questionIds: Array<number>;
            questions: Array<Question>;
            globalFilters: GlobalFilter;
        },
        { getState },
    ) => {
        const { id: surveyId } = selectActiveSurvey(getState());
        const filterParams = getFilterParams(globalFilters, questions);

        const { stat } = await GET('get/word_stat', {
            questionIds: `[${questionIds.toString()}]`,
            ssid: getReportSsid(surveyId, filterParams),
            ...filterParams,
        });

        return stat;
    },
    { notify: false },
);

export const singleAnswerList = createThunkRequest(
    'reports/getAnswerList',
    async (
        {
            surveyId,
            globalFilters,
            answersLimit,
            page,
        }: { surveyId: number; globalFilters: GlobalFilter; answersLimit: number; page: number },
        { getState },
    ) => {
        const state = getState();
        const questions = selectReportsQuestions(state);
        const commonParams = commonReportParams(globalFilters, questions, undefined, page, answersLimit);

        const { count, responses } = await POST('list/response', {
            ...commonParams,
            survey: surveyId,
        });

        return {
            answers: responses.map((ans: any) => mapAnswer(ans)),
            page,
            answersTotalCount: Number(count),
        };
    },
    { notify: false },
);

export const getAnswer = createThunkRequest(
    'reports/getAnswer',
    async ({ surveyId, answerId }: { surveyId: number; answerId: number }) => {
        const [questionsResp, { details, nearby, response }] = await Promise.all([
            POST('list/question', {
                survey: surveyId,
                lang: LocalStorage.get(`survey_${surveyId}_results_lang`) || undefined,
            }),
            POST('list/detail', {
                response: answerId,
            }),
        ]);
        const questions = reorderQuestions(questionsResp.questions);
        const mappedAnswer = mapAnswer(response, questions, details);
        const qp = new URLSearchParams(response.QUERY_PARAMS || '');
        mappedAnswer.reports.forEach((report) => {
            if (report.type === QuestionType.TEXT_BLOCK) {
                report.question.params.textBlock = report.question.params.textBlock
                    ?.replace(/\{Q\-([0-9]+)\}/gi, (_match, id) => {
                        const founded = mappedAnswer.reports.find((r) => r.id.toString() === id);
                        if (founded) {
                            return striptags(
                                founded.value?.toString() || founded.rows?.map((r) => r.value1).join(', ') || '',
                            );
                        }
                        return '';
                    })
                    .replace(/\{LP\-([^}]+)\}/gi, (_, key) => qp.get(key) || '');
            }
            report.question.name = report.question.name
                .replace(/\{Q\-([0-9]+)\}/gi, (_match, id) => {
                    const founded = mappedAnswer.reports.find((r) => r.id.toString() === id);
                    if (founded) {
                        return striptags(
                            founded.value?.toString() || founded.rows?.map((r) => r.value1).join(', ') || '',
                        );
                    }
                    return '';
                })
                .replace(/\{LP\-([^}]+)\}/gi, (_, key) => qp.get(key) || '');

            mappedAnswer.reports = mappedAnswer.reports.map((r) => {
                switch (r.type) {
                    case QuestionType.MATRIX_DROPDOWN_LIST:
                    case QuestionType.DROPDOWN_LIST:
                    case QuestionType.DISTRIBUTE_SCALE:
                    case QuestionType.GROUP_FREE_ANSWERS:
                    case QuestionType.ONE_OF_LIST:
                    case QuestionType.FEW_OF_LIST:
                    case QuestionType.MATRIX_RATING:
                    case QuestionType.MATRIX_SINGLE_ANSWER:
                    case QuestionType.MATRIX_FEW_ANSWERS:
                    case QuestionType.MATRIX_TEXT_ANSWER:
                    case QuestionType.NAME:
                    case QuestionType.RANK:
                    case QuestionType.CSI: {
                        r.rows = r.rows.map((r) => {
                            let parentAnswer: Row | null = null;
                            let parentReport: AnswerReport | null = null;
                            if (r.forwardParent) {
                                mappedAnswer.reports.forEach((report) => {
                                    report.rows.forEach((row) => {
                                        if (row.id?.toString() === r.forwardParent?.toString()) {
                                            parentReport = report;
                                            parentAnswer = row;
                                        }
                                    });
                                });
                            }
                            if (parentAnswer && parentAnswer.type === AnswerType.SELF && parentReport) {
                                r.value1 =
                                    parentReport.rows.find((parentRow) => parentRow.id === r.forwardParent)?.value2 ??
                                    r.value1;
                            }
                            return r;
                        });
                        return r;
                    }
                }
                return r;
            });
            report.rows.forEach((row) => {
                if (row.value1) {
                    row.value1 = row.value1
                        .toString()
                        .replace(/\{Q\-([0-9]+)\}/gi, (_match, id) => {
                            const founded = mappedAnswer.reports.find((r) => r.id.toString() === id);
                            if (founded) {
                                return striptags(
                                    founded.value?.toString() || founded.rows?.map((r) => r.value1).join(', ') || '',
                                );
                            }
                            return '';
                        })
                        .replace(/\{LP\-([^}]+)\}/gi, (_, key) => qp.get(key) || '');
                }
            });

            report.cols.forEach((col) => {
                if (col.headValue) {
                    col.headValue = col.headValue
                        .toString()
                        .replace(/\{Q\-([0-9]+)\}/gi, (_match, id) => {
                            const founded = mappedAnswer.reports.find((r) => r.id.toString() === id);
                            if (founded) {
                                return striptags(
                                    founded.value?.toString() || founded.rows?.map((r) => r.value1).join(', ') || '',
                                );
                            }
                            return '';
                        })
                        .replace(/\{LP\-([^}]+)\}/gi, (_, key) => qp.get(key) || '');
                }
            });
        });
        return {
            answer: mappedAnswer,
            nearbyAnswers: nearby,
        };
    },
    { notify: false },
);

export const removeAnswer = createThunkRequest(
    'reports/removeAnswer',
    async ({ answerId }: { answerId: number }, { dispatch, getState }) => {
        const state = getState();
        const { answers, answersPage, answersTotalCount, globalFilters } = selectReports(state);
        const surveyId: number = selectCurrentSurveyId(state);

        const res = await POST('delete/response', {
            response: answerId,
        });

        if (answersTotalCount > 1 && answers.length === 1) {
            await dispatch(
                singleAnswerList({
                    surveyId,
                    globalFilters,
                    answersLimit: REPORT_RESULTS_PAGE_SIZE,
                    page: answersPage - 1,
                }),
            );
        }

        return res;
    },
);

export const startResult = createThunkRequest(
    'reports/startPublic',
    async ({ shareUrl, surveyUrl, password }: { shareUrl: string; surveyUrl: string; password?: string }) => {
        const {
            createDate,
            main,
            maxEndDate,
            params,
            questionTypes,
            share,
            shareToken,
            surveyId,
            tests,
            passwordAccess,
            filterGroup,
            defaultLang,
            multiLang,
        } = await GET('/survey/v1/share/authorization', { shareUrl, surveyUrl, password }, { absPath: true });

        if (passwordAccess) {
            return { passwordAccess };
        }

        return {
            questionTypes,
            createdDatetime: createDate,
            endDatetime: maxEndDate,
            share,
            surveyId,
            tests: parseJSON(tests, {}, true),
            main: parseJSON(main, {}, true),
            params: parseJSON(params, {}, true),
            passwordAccess,
            shareToken,
            filterGroup,
            defaultLang,
            multiLang,
        };
    },
    { notify: false },
);

export const addCrossItemRequest = ({
    tabId,
    role,
    questionId,
    linkParamId,
}: {
    tabId: number;
    role: number;
    questionId?: number;
    linkParamId?: number;
}) => {
    return POST('add/cross_item', {
        tabId,
        role,
        questionId,
        linkParamId,
    });
};

export const getCrossTabList = createThunkRequest(
    'reports/getCrossTabList',
    async ({ surveyId }: { surveyId: number }) => {
        const { tabs } = await GET('list/cross_tabs', {
            surveyId,
        });

        return tabs;
    },
    { notify: false },
);

export const getCrossStat = createThunkRequest(
    'reports/getCrossStat',
    async (
        {
            tabId,
            start,
            end,
            status = 1,
        }: {
            tabId: number;
            start: string;
            end: string;
            status?: number;
        },
        { getState },
    ) => {
        const state = getState();
        const { globalFilters, questions } = selectReports(state);
        const { id: surveyId } = selectActiveSurvey(state);
        const filterParams = getFilterParams(globalFilters, questions);

        const { crossTable } = await GET('get/cross_stat', {
            tabId,
            start,
            end,
            status,
            ssid: getReportSsid(surveyId, filterParams),
            ...filterParams,
        });

        return { tabId, stat: crossTable };
    },
    { notify: false },
);

export const addCrossTab = createThunkRequest(
    'reports/addCrossTab',
    async (
        {
            surveyId,
            afterQuestion,
            afterParam,
            title,
            questions,
            beforeTab,
            afterTab,
        }: {
            surveyId: number;
            afterQuestion?: number;
            afterParam?: number;
            title: string;
            questions: Array<{ questionId?: number; role: number; linkParamId?: number }>;
            beforeTab?: number;
            afterTab?: number;
        },
        { dispatch },
    ) => {
        const { id } = await POST('add/cross_tab', {
            surveyId,
            afterQuestion,
            afterParam,
            title,
            beforeTab,
            afterTab,
        });

        if (questions[0]) {
            await addCrossItemRequest({
                tabId: id,
                role: questions[0].role,
                questionId: questions[0].questionId,
                linkParamId: questions[0].linkParamId,
            });
        }

        if (questions[1]) {
            await addCrossItemRequest({
                tabId: id,
                role: questions[1].role,
                questionId: questions[1].questionId,
                linkParamId: questions[1].linkParamId,
            });
        }

        await dispatch(getCrossTabList({ surveyId }));

        return {};
    },
);

export const changeCrossTab = createThunkRequest(
    'reports/changeCrossTab',
    async (
        {
            surveyId,
            tabId,
            title,
            questions,
            crossStatStartEnd,
        }: {
            surveyId: number;
            tabId: number;
            title: string;
            questions: Array<{ surveyId: number; questionId?: number; role: number; linkParamId?: number }>;
            crossStatStartEnd: { start: string; end: string; status: number };
        },
        { dispatch },
    ) => {
        await PATCH('change/cross_tab', {
            tabId,
            title,
        });

        if (questions[0]) {
            await PUT('change/tab_question', {
                tabId,
                role: questions[0].role,
                questionId: questions[0].questionId,
                linkParamId: questions[0].linkParamId,
            });
        }

        if (questions[1]) {
            await PUT('change/tab_question', {
                tabId,
                role: questions[1].role,
                questionId: questions[1].questionId,
                linkParamId: questions[1].linkParamId,
            });
        }

        await dispatch(getCrossTabList({ surveyId }));
        await dispatch(getCrossStat({ tabId, ...crossStatStartEnd }));

        return {};
    },
);

export const changeCrossTabParam = createThunkRequest(
    'reports/changeCrossTabParam',
    async (
        {
            tabId,
            params,
            changeAll,
        }: {
            tabId: number;
            params?: Record<string, any>;
            changeAll?: boolean;
        },
        { getState },
    ) => {
        const crossTabs = selectCrossTabs(getState());

        if (changeAll) {
            return crossTabs.map((item) => {
                const newParams = { ...(item?.params || {}), ...params };

                PATCH('change/cross_tab', {
                    tabId: item.id,
                    params: newParams,
                });

                return { ...item, params: newParams };
            });
        } else {
            const findCrossTab = crossTabs?.find((item) => item.id === tabId);
            const newParams = { ...(findCrossTab?.params || {}), ...params };

            await PATCH('change/cross_tab', {
                tabId,
                params: newParams,
            });

            return crossTabs.map((item) => (item.id === tabId ? { ...item, params: newParams } : item));
        }
    },
);

export const deleteCrossTab = createThunkRequest(
    'reports/deleteCrossTab',

    async (tabId: number) => {
        await DELETE(`delete/cross_tab/${tabId}`);

        return { tabId };
    },
);

export const changeLinkParams = createThunkRequest(
    'reports/changeLinkParams',
    async (
        {
            id,
            params,
        }: {
            id: number;
            params: Record<string, any>;
        },
        { getState },
    ) => {
        const link = selectLinkStatsById(id)(getState()) || { params: {} };

        await POST('change/link_params', {
            id,
            params: JSON.stringify({ ...link.params, ...params }),
        });

        return {
            id,
            params: { ...link.params, ...params },
        };
    },
);

export const getAudioFilesStat = createThunkRequest(
    'reports/getAudioFiles',
    async ({ surveyId }: { surveyId: number }, { getState }) => {
        const { globalFilters, questions } = selectReports(getState());
        const commonParams = getFilterParams(globalFilters, questions);

        const { count, size } = await POST(
            'get/files_stat',
            Object.assign(
                {
                    survey: surveyId,
                    audio: true,
                },
                commonParams as any,
            ),
        );

        return { count, size };
    },
);

export const manageCategory = createThunkRequest(
    'reports/manageCategory',
    async (data: { question: number; categories: Array<number>; operation: string; targetCategory: string }) => {
        const { count, size } = await POST('manage/category', data);

        return { count, size };
    },
);

export const getCategories = createThunkRequest(
    'reports/getCategories',
    async (data: { questionId: number }) => {
        const { categories } = await POST('list/category', data);

        return { categories: categories ? sortBy(categories.map(mapCategory), 'name') : [] };
    },
    { notify: false, notifyError: true },
);
